import type {
  EmailApiProvider,
  EmailSmtpViaBasicAuthProvider,
  EmailSmtpViaOauthProvider,
  ProviderFormValues,
  SmsProvider,
  TelegramProvider,
  WebAppProvider,
  WhatsappMetaProvider,
  WhatsappProvider,
  ViberProvider,
  LineProvider,
} from '../types/provider';
import { TEAM_CHAT_PLATFORMS } from '../constants';

const appendIfDefined = (fd: FormData, key: string, value: unknown): void => {
  if (value === undefined || value === null) return;
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  fd.append(key, String(value));
};

const appendEmailBasicAuth = (fd: FormData, m: EmailSmtpViaBasicAuthProvider): void => {
  const prefix = 'emailSmtpViaBasicAuthProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.username`, m.username);
  appendIfDefined(fd, `${prefix}.password`, m.password);
  appendIfDefined(fd, `${prefix}.from`, m.from);
  appendIfDefined(fd, `${prefix}.port`, m.port);
  appendIfDefined(fd, `${prefix}.hostname`, m.hostname);
  appendIfDefined(fd, `${prefix}.securityType`, m.securityType);
};

const appendEmailOauth2 = (fd: FormData, m: EmailSmtpViaOauthProvider): void => {
  const prefix = 'emailSmtpViaOauthProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.hostname`, m.hostname);
  appendIfDefined(fd, `${prefix}.privateKey`, m.privateKey);
  appendIfDefined(fd, `${prefix}.securityType`, m.securityType);
  appendIfDefined(fd, `${prefix}.serviceAccountEmail`, m.serviceAccountEmail);
  appendIfDefined(fd, `${prefix}.userToImpersonate`, m.userToImpersonate);
  appendIfDefined(fd, `${prefix}.port`, m.port);
};

const appendEmailApi = (fd: FormData, m: EmailApiProvider): void => {
  const prefix = 'emailApiProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.apiKey`, m.apiKey);
  appendIfDefined(fd, `${prefix}.apiEndpoint`, m.apiEndpoint);
  appendIfDefined(fd, `${prefix}.customHeaders`, m.customHeaders);
  appendIfDefined(fd, `${prefix}.from`, m.from);
};

const appendSms = (fd: FormData, m: SmsProvider): void => {
  const prefix = 'smsProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.apiKey`, m.apiKey);
  appendIfDefined(fd, `${prefix}.senderId`, m.senderId);
  appendIfDefined(fd, `${prefix}.apiEndpoint`, m.apiEndpoint);
  appendIfDefined(fd, `${prefix}.apisecret`, m.apisecret);
};

const appendWhatsapp = (fd: FormData, m: WhatsappProvider): void => {
  const prefix = 'whatsappProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.businessPhoneNumber`, m.businessPhoneNumber);
  appendIfDefined(fd, `${prefix}.apiKey`, m.apiKey);
  appendIfDefined(fd, `${prefix}.apiEndpoint`, m.apiEndpoint);
  appendIfDefined(fd, `${prefix}.apisecret`, m.apisecret);
};

const appendWhatsappMeta = (fd: FormData, m: WhatsappMetaProvider): void => {
  const prefix = 'whatsappMetaProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.phoneNumberId`, m.phoneNumberId);
  appendIfDefined(fd, `${prefix}.wabaId`, m.wabaId);
  appendIfDefined(fd, `${prefix}.accessToken`, m.accessToken);
  appendIfDefined(fd, `${prefix}.graphApiVersion`, m.graphApiVersion);
  appendIfDefined(fd, `${prefix}.apiEndpoint`, m.apiEndpoint);
  appendIfDefined(fd, `${prefix}.metaBusinessPhoneNumber`, m.metaBusinessPhoneNumber);
};

const appendTelegram = (fd: FormData, m: TelegramProvider): void => {
  const prefix = 'telegramProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.botToken`, m.botToken);
};

const appendViber = (fd: FormData, m: ViberProvider): void => {
  const prefix = 'viberProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.botToken`, m.botToken);
  appendIfDefined(fd, `${prefix}.senderName`, m.senderName);
};

const appendLine = (fd: FormData, m: LineProvider): void => {
  const prefix = 'lineProvider';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  appendIfDefined(fd, `${prefix}.channelAccessToken`, m.channelAccessToken);
};

const appendWebApp = (fd: FormData, m: WebAppProvider): void => {
  const prefix = 'webAppProviderModel';
  appendIfDefined(fd, `${prefix}.id`, m.id);
  if (m.uploadFile instanceof File) {
    fd.append(`${prefix}.uploadFile`, m.uploadFile);
  }
  if (m.fileContain) fd.append(`${prefix}.fileContain`, String(m.fileContain));
};

// A channel is "present" (configured) when its sub-provider was loaded (has an
// id). Channel block: a channel that is present but toggled off is sent anyway
// as blocked (isActive=false) instead of being removed.
const present = (m?: { id?: string }): boolean => Boolean(m?.id);
const waPresent = (v: ProviderFormValues): boolean =>
  present(v.whatsappProvider) || present(v.whatsappMetaProvider);
const teamChatConfigured = (v: ProviderFormValues): boolean =>
  TEAM_CHAT_PLATFORMS.some(
    (p) => v.teamChat?.[p.key]?.enabled || v.teamChat?.[p.key]?.present,
  );

const deriveCommunicationType = (values: ProviderFormValues): string[] => {
  const types: string[] = [];
  if (values.sms || present(values.smsProvider)) types.push('sms');
  if (values.email !== 'none') types.push('email');
  if (values.whatsApp || waPresent(values)) types.push('whatsapp');
  if (values.telegram || present(values.telegramProvider)) types.push('telegram');
  if (values.viber || present(values.viberProvider)) types.push('viber');
  if (values.line || present(values.lineProvider)) types.push('line');
  if (values.push || present(values.webAppProviderModel)) types.push('push');
  if (teamChatConfigured(values)) types.push('teamChat');
  return types;
};

// Each configured platform → one TeamChatProviders[i] entry (active or blocked).
// Platform goes as the numeric enum value; an empty webhook on edit keeps the
// stored one; isActive carries the active/blocked toggle.
const appendTeamChat = (fd: FormData, values: ProviderFormValues): void => {
  let i = 0;
  for (const p of TEAM_CHAT_PLATFORMS) {
    const entry = values.teamChat?.[p.key];
    if (!entry || !(entry.enabled || entry.present)) continue;
    fd.append(`teamChatProviders[${i}].platform`, String(p.enumValue));
    fd.append(`teamChatProviders[${i}].webhookUrl`, entry.webhookUrl ?? '');
    fd.append(`teamChatProviders[${i}].isActive`, String(Boolean(entry.enabled)));
    i += 1;
  }
};

export const buildProviderFormData = (values: ProviderFormValues): FormData => {
  const fd = new FormData();
  fd.append('providerName', values.providerName);
  fd.append('providerStatus', values.providerStatus);
  fd.append('priority', String(values.priority ?? 0));
  fd.append('retries', String(values.retries ?? 0));

  deriveCommunicationType(values).forEach((type) =>
    fd.append('communicationType', type)
  );

  if (values.id) fd.append('id', values.id);

  switch (values.email) {
    case 'emailSmtpViaBasicAuthProvider':
      appendEmailBasicAuth(fd, values.emailSmtpViaBasicAuthProvider);
      break;
    case 'emailSmtpViaOauthProvider':
      appendEmailOauth2(fd, values.emailSmtpViaOauthProvider);
      break;
    case 'api':
      appendEmailApi(fd, values.emailApiProvider);
      break;
    case 'none':
    default:
      break;
  }

  if (values.sms || present(values.smsProvider)) {
    appendSms(fd, values.smsProvider);
    fd.append('smsProvider.isActive', String(Boolean(values.sms)));
  }
  if (values.whatsApp || waPresent(values)) {
    if (values.whatsAppProviderType === 'twilio') {
      appendWhatsapp(fd, values.whatsappProvider);
      fd.append('whatsappProvider.isActive', String(Boolean(values.whatsApp)));
    } else if (values.whatsAppProviderType === 'meta') {
      appendWhatsappMeta(fd, values.whatsappMetaProvider);
      fd.append(
        'whatsappMetaProvider.isActive',
        String(Boolean(values.whatsApp)),
      );
    }
  }
  if (values.telegram || present(values.telegramProvider)) {
    appendTelegram(fd, values.telegramProvider);
    fd.append('telegramProvider.isActive', String(Boolean(values.telegram)));
  }
  if (values.viber || present(values.viberProvider)) {
    appendViber(fd, values.viberProvider);
    fd.append('viberProvider.isActive', String(Boolean(values.viber)));
  }
  if (values.line || present(values.lineProvider)) {
    appendLine(fd, values.lineProvider);
    fd.append('lineProvider.isActive', String(Boolean(values.line)));
  }
  if (values.push || present(values.webAppProviderModel)) {
    appendWebApp(fd, values.webAppProviderModel);
    fd.append('webAppProviderModel.isActive', String(Boolean(values.push)));
  }
  appendTeamChat(fd, values);

  return fd;
};

export default buildProviderFormData;
